import { useReducedMotion } from 'framer-motion';
import { memo, useEffect, useState } from 'react';

import { Flex, rem, Skeleton, Text } from '@bonusly/design-system';

import { Bizy, useReduceBizyMotionPreference, type BizyState } from '../../bizy';

export interface BizyTimelineSkeletonProps {
	/** Announced while the placeholder stands in for the timeline. */
	label: string;
}

// One entry per turn, shaped like the real timeline: assistant turns are
// full-width prose (BizyTimelineMessage renders them left-aligned and
// unbubbled), user turns are short right-aligned bubbles. The pattern repeats
// to fill the panel, so the varied line counts are what keep it from reading
// as a repeat.
const TURN_PATTERN: { role: 'user' | 'assistant'; lines: string[] }[] = [
	{ role: 'user', lines: ['58%'] },
	{ role: 'assistant', lines: ['100%', '94%', '72%'] },
	{ role: 'user', lines: ['42%'] },
	{ role: 'assistant', lines: ['100%', '88%', '96%', '61%'] },
	{ role: 'user', lines: ['66%'] },
	{ role: 'assistant', lines: ['92%', '100%', '78%'] },
];

// Rough average of a short bubble row and a multi-line prose block. Only used
// to decide how many turns it takes to fill the panel — the column is clipped
// and faded at the top, so being a row over or under is invisible. Estimating
// from the viewport beats measuring the container: no layout read, no observer,
// and the panel is always within a header's height of the window.
const ESTIMATED_TURN_HEIGHT_PX = 70;

// Chakra's default skeleton greys are heavy enough to read as content. These
// keep the placeholder quiet so the eye lands on Bizy instead.
const BAR_COLORS = { startColor: 'bg.soft', endColor: 'bg.subtle' } as const;

// Dissolves the older turns into the panel background instead of ending them on
// a hard edge, which is what made the untouched space above read as unfinished.
const FADE_OUT_UPWARD = 'linear-gradient(to bottom, transparent 0%, black 50%)';

// Bizy hunts for the conversation while it loads: a glance one way, a look
// around, a glance the other. Long holds — this is ambient company for the
// wait, not a progress indicator.
const SEARCH_POSE_LOOP = [
	{ state: 'searchingLeft', holdMs: 1500 },
	{ state: 'searching', holdMs: 900 },
	{ state: 'searchingRight', holdMs: 1500 },
	{ state: 'searching', holdMs: 900 },
] as const satisfies readonly { state: BizyState; holdMs: number }[];

// Matches the thinking indicator's breathing label in the timeline below.
const LABEL_PULSE_SX = {
	'@keyframes pulse': {
		'0%, 100%': { opacity: 1 },
		'50%': { opacity: 0.4 },
	},
	animation: 'pulse 1.5s ease-in-out infinite',
} as const;

/**
 * Cycles the search poses, holding the opening glance when motion is reduced so
 * Bizy still reads as looking for something without the animation.
 */
function useBizySearchPose(): { pose: BizyState; isMotionReduced: boolean } {
	const prefersReducedMotion = useReducedMotion();
	const [reduceBizyMotion] = useReduceBizyMotionPreference();
	const isMotionReduced = Boolean(prefersReducedMotion) || reduceBizyMotion;
	const [step, setStep] = useState(0);

	useEffect(() => {
		if (isMotionReduced) {
			setStep(0);
			return;
		}

		const timer = setTimeout(() => setStep((current) => (current + 1) % SEARCH_POSE_LOOP.length), SEARCH_POSE_LOOP[step].holdMs);
		return () => clearTimeout(timer);
	}, [isMotionReduced, step]);

	return { pose: SEARCH_POSE_LOOP[step].state, isMotionReduced };
}

/**
 * Propless and memoized so the pose loop's ~1s re-renders stay confined to
 * Bizy instead of reconciling every bar.
 *
 * `flexShrink=0` throughout: once the column overflows, flex's default shrink
 * would squash the bars toward zero height rather than letting the oldest ones
 * slide up under the fade.
 */
const PlaceholderTurns = memo(function PlaceholderTurns({ isMotionReduced }: { isMotionReduced: boolean }) {
	// Sampled once per mount, not tracked: the placeholder is short-lived, and a
	// resize mid-load isn't worth rebuilding every bar for.
	const [turns] = useState(() => {
		const turnsToFillPanel = Math.ceil(window.innerHeight / ESTIMATED_TURN_HEIGHT_PX);
		return Array.from({ length: turnsToFillPanel }, (_, index) => TURN_PATTERN[index % TURN_PATTERN.length]);
	});
	// Chakra's Skeleton animates its colour on an infinite loop with no regard for
	// prefers-reduced-motion, so we have to still it ourselves.
	const bar = { ...BAR_COLORS, ...(isMotionReduced && { animation: 'none' }) };

	return (
		<>
			{turns.map((turn, turnIndex) =>
				turn.role === 'user' ? (
					<Skeleton key={turnIndex} {...bar} alignSelf="flex-end" w={turn.lines[0]} maxW="85%" h={rem(34)} borderRadius={12} flexShrink={0} />
				) : (
					<Flex key={turnIndex} direction="column" w="100%" gap={2} flexShrink={0}>
						{turn.lines.map((width, lineIndex) => (
							<Skeleton key={lineIndex} {...bar} w={width} h={rem(11)} borderRadius="full" />
						))}
					</Flex>
				)
			)}
		</>
	);
});

/**
 * Stands in for the conversation while the first timeline page is in flight.
 *
 * Bottom-anchored because that's where the real timeline settles — the init
 * load jumps to the newest turn — so content arrives roughly where the
 * placeholder sat instead of dropping in from the top of the viewport. Bizy
 * sits where the in-chat Bizy will be once messages land, for the same reason.
 *
 * The turn column clips its own overflow, so however many turns it takes to
 * fill the panel, the placeholder never grows the scroll container.
 */
export function BizyTimelineSkeleton({ label }: BizyTimelineSkeletonProps) {
	const { pose, isMotionReduced } = useBizySearchPose();

	return (
		<Flex direction="column" flex={1} minH={0} role="status" aria-label={label} data-component="bizy-timeline-skeleton">
			<Flex
				direction="column"
				flex={1}
				minH={0}
				gap={5}
				justify="flex-end"
				overflow="hidden"
				sx={{ maskImage: FADE_OUT_UPWARD, WebkitMaskImage: FADE_OUT_UPWARD }}
			>
				<PlaceholderTurns isMotionReduced={isMotionReduced} />
			</Flex>

			<Flex direction="column" align="center" gap={1} pt={3} pb={2} flexShrink={0}>
				<Bizy state={pose} size="md" />
				<Text fontSize="xs" fontWeight="medium" color="text.softest" sx={isMotionReduced ? undefined : LABEL_PULSE_SX}>
					{label}
				</Text>
			</Flex>
		</Flex>
	);
}
